import React, { createContext, useCallback, useContext, useState } from "react";
import UserProfileModal from "../components/UserProfileModal";

const UserProfileModalContext = createContext(null);

export function UserProfileModalProvider({ children }) {
  const [modalState, setModalState] = useState({
    isOpen: false,
    userId: null,
    swipeStatus: null,
    prefetchedProfile: null,
    disableActions: false,
    isProcessing: false,
    onLike: undefined,
    onSkip: undefined,
  });

  const openUserProfile = useCallback((options) => {
    setModalState((prev) => ({
      ...prev,
      ...options,
      isOpen: true,
    }));
  }, []);

  const closeUserProfile = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
      isProcessing: false,
    }));
  }, []);

  return (
    <UserProfileModalContext.Provider
      value={{ openUserProfile, closeUserProfile }}
    >
      {children}
      <UserProfileModal
        userId={modalState.userId}
        isOpen={modalState.isOpen}
        onClose={closeUserProfile}
        swipeStatus={modalState.swipeStatus}
        prefetchedProfile={modalState.prefetchedProfile}
        disableActions={modalState.disableActions || modalState.isProcessing}
        onLike={
          modalState.onLike
            ? async () => {
                // Prevent duplicate clicks
                setModalState((prev) => {
                  if (prev.isProcessing) return prev;
                  return { ...prev, isProcessing: true };
                });

                try {
                  await modalState.onLike();
                } catch (error) {
                  // Log but always close for smooth UX
                  // eslint-disable-next-line no-console
                  console.error(
                    "[UserProfileModalContext] onLike error",
                    error,
                  );
                } finally {
                  setModalState((prev) => ({
                    ...prev,
                    isProcessing: false,
                    isOpen: false,
                  }));
                }
              }
            : undefined
        }
        onSkip={
          modalState.onSkip
            ? async () => {
                setModalState((prev) => {
                  if (prev.isProcessing) return prev;
                  return { ...prev, isProcessing: true };
                });

                try {
                  await modalState.onSkip();
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error(
                    "[UserProfileModalContext] onSkip error",
                    error,
                  );
                } finally {
                  setModalState((prev) => ({
                    ...prev,
                    isProcessing: false,
                    isOpen: false,
                  }));
                }
              }
            : undefined
        }
      />
    </UserProfileModalContext.Provider>
  );
}

export function useUserProfileModal() {
  const ctx = useContext(UserProfileModalContext);
  if (!ctx) {
    throw new Error(
      "useUserProfileModal must be used within a UserProfileModalProvider",
    );
  }
  return ctx;
}
